import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLocationContext } from "@/contexts/LocationContext";
import {
  canonicalHandle,
  resolveActivePrice,
  resolveAvailabilityBehaviour,
  resolveGlobalAvailability,
  type AustralianState,
  type AvailabilityBehaviour,
  type GlobalAvailabilitySummary,
  type PublicAvailabilityRow,
  type ResolvedPrice,
} from "@/lib/availability";

// Freshness model: REFRESH-BASED, not live.
// Admin stock/pricing edits are picked up the next time a visitor loads or
// refreshes a page (every availability query refetches on mount). There is no
// realtime channel and no background polling, so nothing changes under a
// customer mid-session.
const AVAILABILITY_STALE_MS = 0;


async function fetchPublicAvailabilityBatch(
  state: AustralianState | null,
): Promise<PublicAvailabilityRow[]> {
  const { data, error } = await supabase.rpc("get_public_product_availability", {
    _product_handle: undefined,
    _state: state,
  });
  if (error) throw error;
  return (data ?? []) as PublicAvailabilityRow[];
}

// Fetches the sanitized public availability row for a product at the
// currently selected location. Returns null when no location is selected
// or no row exists for that product/location.
export function useProductAvailability(productHandle: string | null | undefined) {
  const { state } = useLocationContext();
  useAvailabilitySubscription();
  const handle = canonicalHandle(productHandle);

  const query = useQuery({
    queryKey: ["product-availability-batch", state],
    enabled: Boolean(state),
    staleTime: AVAILABILITY_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",
    queryFn: () => fetchPublicAvailabilityBatch(state),

  });

  const row = handle
    ? (query.data ?? []).find((candidate) => canonicalHandle(candidate.product_handle) === handle) ?? null
    : null;

  return {
    state,
    row,
    isLoading: query.isLoading,
    hasLocation: Boolean(state),
  };
}

/**
 * Fetches every branch row (all four states) for a product, regardless of the
 * selected location, and computes the global availability summary. Powers the
 * "globally Sold Out" state shown before a customer picks a location.
 */
export function useGlobalProductAvailability(productHandle: string | null | undefined): {
  rows: PublicAvailabilityRow[];
  summary: GlobalAvailabilitySummary;
  isLoading: boolean;
} {
  const handle = canonicalHandle(productHandle);
  useAvailabilitySubscription();
  const query = useQuery({
    queryKey: ["product-availability-global-batch"],
    enabled: Boolean(handle),
    staleTime: AVAILABILITY_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",

    queryFn: () => fetchPublicAvailabilityBatch(null),
  });

  const rows = handle
    ? (query.data ?? []).filter((candidate) => canonicalHandle(candidate.product_handle) === handle)
    : [];
  return {
    rows,
    summary: resolveGlobalAvailability(rows),
    isLoading: query.isLoading,
  };
}

/**
 * Central resolved-price hook. Returns the single active unit price for a
 * product at the currently selected location, resolving:
 *   location sale → location regular → inherited website/base price → contact.
 * Every surface (title price, calculator, cart, quote snapshot) should use
 * this so prices never disagree.
 */
export function useResolvedProductPrice(
  productHandle: string | null | undefined,
  basePrice: number | null,
): ResolvedPrice & { isLoading: boolean; hasLocation: boolean } {
  const { row, state, isLoading, hasLocation } = useProductAvailability(productHandle);
  const resolved = resolveActivePrice(row, state, basePrice);
  return { ...resolved, isLoading, hasLocation };
}

/**
 * Central product action-state hook. Combines the resolved price with the
 * resolved customer-facing behaviour (add-to-cart gating, sold-out, quote-first)
 * so every surface behaves identically for a given product/location.
 */
export function useProductActionState(
  productHandle: string | null | undefined,
  basePrice: number | null,
): {
  price: ResolvedPrice;
  behaviour: AvailabilityBehaviour;
  global: GlobalAvailabilitySummary;
  row: PublicAvailabilityRow | null;
  state: ReturnType<typeof useProductAvailability>["state"];
  isLoading: boolean;
  hasLocation: boolean;
} {
  const { row, state, isLoading, hasLocation } = useProductAvailability(productHandle);
  const { summary: global } = useGlobalProductAvailability(productHandle);
  const price = resolveActivePrice(row, state, basePrice);
  const hasInheritedDefault = basePrice != null && basePrice > 0;
  let behaviour = resolveAvailabilityBehaviour(row, hasLocation, hasInheritedDefault);

  // No location chosen but the product is unavailable in ALL four states →
  // treat as globally Sold Out everywhere on the site.
  if (!hasLocation && global.isGloballySoldOut) {
    behaviour = {
      status: "unavailable",
      isSoldOut: true,
      canAddToCart: false,
      quoteFirst: true,
      cartButtonLabel: "Sold Out",
      soldOutBadge: "Sold Out",
      message: global.message,
      needsNote: true,
    };
  }

  return { price, behaviour, global, row, state, isLoading, hasLocation };
}

export interface CartAvailabilityInput {
  variantId: string;
  productHandle: string | null | undefined;
  basePrice: number;
  quantity: number;
  selectedOptions?: Array<{ name: string; value: string }>;
}

export interface CartAvailabilityLine {
  item: CartAvailabilityInput;
  row: PublicAvailabilityRow | null;
  behaviour: AvailabilityBehaviour;
  unitPrice: number;
  lineTotal: number;
  blocked: boolean;
}

function parseCartArea(options: Array<{ name: string; value: string }> | undefined): number | null {
  const areaValue = options?.find((opt) => opt.name.toLowerCase() === "area")?.value;
  if (!areaValue) return null;
  const parsed = Number.parseFloat(areaValue.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function resolveCartUnitPrice(item: CartAvailabilityInput, row: PublicAvailabilityRow | null, state: AustralianState | null): number {
  const resolved = resolveActivePrice(row, state, item.basePrice);
  if (resolved.amount == null || resolved.isContact) return item.basePrice;

  // Artificial grass cart items store width/length/area. Location price is per
  // m², so recalculate the line snapshot from the latest admin price.
  const area = parseCartArea(item.selectedOptions);
  if (area && resolved.source === "location") return resolved.amount * area;

  return resolved.amount;
}

export function useCartAvailabilitySync(items: CartAvailabilityInput[]): {
  lines: CartAvailabilityLine[];
  blockedLines: CartAvailabilityLine[];
  hasBlockedItems: boolean;
  isLoading: boolean;
  totalPrice: number;
} {
  const { state } = useLocationContext();
  useAvailabilitySubscription();
  const query = useQuery({
    queryKey: ["product-availability-batch", state],
    enabled: Boolean(state && items.length > 0),
    staleTime: AVAILABILITY_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",
    queryFn: () => fetchPublicAvailabilityBatch(state),
  });
  const rows = query.data ?? [];

  const lines = items.map((item) => {
    const handle = canonicalHandle(item.productHandle);
    const row = handle
      ? rows.find((candidate) => canonicalHandle(candidate.product_handle) === handle) ?? null
      : null;
    const hasInheritedDefault = item.basePrice > 0;
    const behaviour = resolveAvailabilityBehaviour(row, Boolean(state), hasInheritedDefault);
    const unitPrice = resolveCartUnitPrice(item, row, state);
    const lineTotal = unitPrice * item.quantity;
    const checking = Boolean(state && query.isLoading);
    return {
      item,
      row,
      behaviour,
      unitPrice,
      lineTotal,
      // Only truly SOLD OUT (unavailable) lines hard-block the cart quote submit
      // and quantity controls. Quote-first statuses (special_order, call_to_confirm)
      // must still flow through the cart-quote form — that IS the quote path.
      blocked: checking || behaviour.isSoldOut,
    };
  });

  const blockedLines = lines.filter((line) => line.blocked);
  return {
    lines,
    blockedLines,
    hasBlockedItems: blockedLines.length > 0,
    isLoading: query.isLoading,
    totalPrice: lines.reduce((sum, line) => sum + line.lineTotal, 0),
  };
}

// ---------------------------------------------------------------------------
// No realtime / no live updates.
//
// Availability and pricing are read fresh on every page load or refresh. The
// previous realtime channel (and its `product_availability_sync_events`
// listener) has been removed so admin edits never mutate a page a customer is
// already looking at, and the database carries no subscription load.
// ---------------------------------------------------------------------------

/** No-op. Kept so existing call sites compile; freshness comes from refresh. */
export function useAvailabilitySubscription() {
  // intentionally empty
}

/** @deprecated No-op. Availability is refresh-based. */
export function useAvailabilityRealtimeSync() {
  // intentionally empty
}
